import { Input, Tag, TagCloseButton, TagLabel, Text, Wrap, WrapItem } from 'design-system';
import React, { useState } from 'react';

export interface MultiValueInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  validate?: (item: string) => string | undefined;
  placeholder?: string;
  inputAriaLabel?: string;
}

export const MultiValueInput = ({ value, onChange, validate, placeholder, inputAriaLabel }: MultiValueInputProps) => {
  const [pending, setPending] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const addPending = () => {
    const item = pending.trim().replace(/,$/, '');
    if (!item) return;

    const validationError = validate?.(item);
    if (validationError) {
      setError(validationError);
      return;
    }

    const exists = value.some((v) => v.toLowerCase() === item.toLowerCase());
    if (!exists) onChange([...value, item]);

    setPending('');
    setError(undefined);
  };

  const removeItem = (item: string) => {
    onChange(value.filter((v) => v !== item));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addPending();
    }

    if (e.key === 'Backspace' && !pending && value.length) {
      removeItem(value[value.length - 1]);
    }
  };

  return (
    <>
      {!!value.length && (
        <Wrap mb='xs'>
          {value.map((item) => (
            <WrapItem key={item}>
              <Tag size='sm'>
                <TagLabel>{item}</TagLabel>
                <TagCloseButton aria-label={`remove-${item}`} onClick={() => removeItem(item)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <Input
        value={pending}
        placeholder={placeholder}
        aria-label={inputAriaLabel}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setPending(e.target.value);
          if (error) setError(undefined);
        }}
        onKeyDown={onKeyDown}
        onBlur={addPending}
      />

      {!!error && (
        <Text color='red.500' fontSize='sm' mt='xs'>
          {error}
        </Text>
      )}
    </>
  );
};
